/**
 * RaceDebugOverlay — lightweight debug panel that surfaces live GM Race
 * state during playtesting.
 *
 * Shows on any `gm_race_*` map when the race debug overlay is enabled (ON
 * by default while the arc is in testing). Toggled via the DebugToolbox
 * "🏁 Race" button (Shift+D → 🏁). The toolbox itself is gated by
 * DevConfig.systems.debugToolbox, so the overlay only appears on dev/PTR
 * builds where the toolbox renders.
 *
 * This is the thin orchestrator — state gathering lives in
 * `useRaceDebugState`, EventBus wiring in `useRaceDebugEventLog`, and each
 * section is its own component under `sections/`.
 */

import React, { useState } from 'react';
import { RACE_DEBUG } from '../../../../shared/config/race-debug.config';
import {
  setRaceDebugOverlayEnabled,
  clearRaceDebugLog,
} from '../../../systems/race/RaceDebugOverlayState';
import { panelBtnStyle } from './RaceDebugPrimitives';
import { RenderToggles } from './RenderToggles';
import { useRaceDebugState } from './useRaceDebugState';
import { useRaceDebugEventLog } from './useRaceDebugEventLog';
import { SessionSection } from './sections/SessionSection';
import { PlayerStateSection } from './sections/PlayerStateSection';
import { HazardsSection } from './sections/HazardsSection';
import { PowerupsSection } from './sections/PowerupsSection';
import { AbilityGateSection } from './sections/AbilityGateSection';
import { EventLogSection } from './sections/EventLogSection';
import { ConnectionSection } from './sections/ConnectionSection';

export const RaceDebugOverlay: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);

  // Wire EventBus listeners for the rolling event log (once).
  useRaceDebugEventLog();

  // Poll live state at ~4Hz. Returns null when overlay should not render.
  const snap = useRaceDebugState();

  if (!snap) return null;

  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    top: 40,
    right: 8,
    zIndex: 9990,
    background: `rgba(0, 0, 0, ${RACE_DEBUG.panelAlpha})`,
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '6px',
    padding: '8px 10px',
    fontFamily: 'monospace',
    fontSize: '11px',
    color: '#ccc',
    maxWidth: '380px',
    maxHeight: '70vh',
    overflowY: 'auto',
    pointerEvents: 'auto',
    lineHeight: '1.5',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    paddingBottom: '4px',
  };

  if (collapsed) {
    return (
      <div style={{ ...panelStyle, padding: '4px 8px', cursor: 'pointer' }}
           onClick={() => setCollapsed(false)}>
        <span style={{ color: '#2ecc71' }}>🏁 Race Debug</span>
        <span style={{ color: '#666', marginLeft: '8px' }}>expand</span>
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      <div style={headerStyle}>
        <span style={{ color: '#2ecc71', fontWeight: 'bold' }}>🏁 Race Debug</span>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={() => clearRaceDebugLog()} style={panelBtnStyle} title="Clear event log">
            clear
          </button>
          <button onClick={() => setCollapsed(true)} style={panelBtnStyle} title="Collapse">
            collapse
          </button>
          <button
            onClick={() => setRaceDebugOverlayEnabled(false)}
            style={{ ...panelBtnStyle, color: '#e74c3c' }}
            title="Turn off race debug overlay"
          >
            off
          </button>
        </div>
      </div>

      <RenderToggles />

      {RACE_DEBUG.session.enabled && (
        <SessionSection mapId={snap.mapId} gmState={snap.gmState} />
      )}
      {RACE_DEBUG.playerState.enabled && (
        <PlayerStateSection gmState={snap.gmState} gmLevel={snap.localGmLevel} />
      )}
      {RACE_DEBUG.hazards.enabled && (
        <HazardsSection hazards={snap.hazards} />
      )}
      {RACE_DEBUG.powerups.enabled && (
        <PowerupsSection powerups={snap.powerups} />
      )}
      {RACE_DEBUG.abilityGate.enabled && (
        <AbilityGateSection
          gateActive={snap.gateActive}
          gateAllowedCount={snap.gateAllowedCount}
        />
      )}
      {RACE_DEBUG.eventLog.enabled && (
        <EventLogSection log={snap.log} />
      )}
      {RACE_DEBUG.desync.enabled && (
        <ConnectionSection nm={snap.nm} room={snap.room} tracer={snap.tracer} />
      )}
    </div>
  );
};
